import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import * as _ from "lodash";
import { first, isEqual } from "lodash";
import { concat, of, Subject } from "rxjs";
import { first as firstPipe, map, takeUntil } from "rxjs/operators";
import { PersonLanguageEnum } from "src/app/shared/Models/enums/PersonLanguageEnum";
import { PersonPersonTypeEnum } from "src/app/shared/Models/enums/PersonPersonTypeEnum";
import { Person } from "src/app/shared/Models/read";
import { AddNewPersonToBuildingDto } from "src/app/shared/Models/write/AddNewPersonToBuildingDto";
import { PhoneNumberRegexString } from "src/app/shared/regex/regexes";
import * as uuid from "uuid";
import { ChangeDocumentContact } from "../../models/changeDocumentContact";
import { DocumentContactDTO } from "../../models/documentContactDTO";
import { DocumentListItemDTO } from "../../models/documentListItemsDTO";
import { DocumentListService } from "../../services/documentList.service";

@Component({
  selector: "app-document-contact-info-modal",
  templateUrl: "./document-contact-info-modal.component.html",
  styleUrls: ["./document-contact-info-modal.component.scss"],
})
export class DocumentEditContactInfoComponent implements OnInit, OnDestroy {
  @Input() documentItem: DocumentListItemDTO;
  stopTrackingFormChanges: Subject<any> = new Subject<any>();
  personTypes = PersonPersonTypeEnum;
  contactForm: FormGroup = new FormGroup({});
  personLanguageOptions: {
    displayName: string;
    value: PersonLanguageEnum;
  }[] = Object.keys(PersonLanguageEnum)
    .filter((l) => isNaN(+l))
    .map((l) => {
      return { displayName: l, value: PersonLanguageEnum[l] };
    });
  personTypeOptions: {
    displayName: string;
    value: PersonPersonTypeEnum;
  }[] = Object.keys(PersonPersonTypeEnum)
    .filter((l) => isNaN(+l))
    .map((l) => {
      return { displayName: l, value: PersonPersonTypeEnum[l] };
    });
  updatedContacts: { [id: string]: DocumentContactDTO } = {};
  selectedContactId: string;
  addNewContactScreenActive: boolean;
  newContactIds = [];

  constructor(
    private readonly _fb: FormBuilder,
    private readonly documentListService: DocumentListService,
    private readonly activeModal: NgbActiveModal
  ) {}

  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnInit() {
    this.documentItem.contacts.forEach(
      (c) => (this.updatedContacts[c.id] = { ...c })
    );
    this.buildForm();
  }

  private buildForm() {
    this.contactForm = this._fb.group({
      name: ["", Validators.required],
      phone: [
        "",
        [Validators.required, Validators.pattern(PhoneNumberRegexString)],
      ],
      email: ["", [Validators.required, Validators.email]],
      language: ["", [Validators.required]],
      type: ["", [Validators.required]],
      street: ["", Validators.required],
      number: ["", [Validators.required, Validators.pattern("^[0-9]*$")]],
      suffix: [""],
      postalCode: [
        "",
        [Validators.required, Validators.pattern("^(([1-9])(\\d{3}))$")],
      ],
      city: ["", Validators.required],
    });

    if (this.documentItem.contacts.length === 1)
      this.onSelectContact(first(this.documentItem.contacts).id);
  }

  onSelectContact(id: string) {
    if (!this.validateForm()) return;

    this.saveContact()
      .pipe(firstPipe())
      .subscribe(() => {
        const selectedPerson = this.documentItem.contacts.find(
          (x) => x.id == id
        );
        this.stopTrackingFormChanges.next(true);
        this.selectedContactId = id;
        this.contactForm.patchValue({
          ...selectedPerson,
        });

        this.contactForm.valueChanges
          .pipe(takeUntil(this.stopTrackingFormChanges))
          .subscribe((res) => {
            this.updatedContacts[id] = { ...selectedPerson, ...res };
          });
        this.contactForm.markAsTouched({ onlySelf: true });
      });
  }

  saveContact() {
    const keysToUpdate = this.detectChanges();
    if (keysToUpdate.length === 0) return of({});

    if (this.newContactIds.some((i) => i === this.selectedContactId)) {
      const newContact = new AddNewPersonToBuildingDto().deserialize(
        this.updatedContacts[this.selectedContactId]
      );
      newContact.language = this.personLanguageOptions.find(
        (x) => x.value == this.contactForm.get("language").value
      ).displayName;
      newContact.personType = this.personTypeOptions.find(
        (x) => x.value == this.contactForm.get("type").value
      ).displayName;
      newContact.buildingId = this.documentItem.buildingId;
      newContact.personId = this.selectedContactId;
      this.newContactIds = this.newContactIds.filter(
        (i) => i !== this.selectedContactId
      );
      return concat(
        this.documentListService.saveNewContactToList(newContact),
        this.updateDocumentContact()
      );
    }
    return this.updateDocumentContact();
  }

  updateDocumentContact() {
    const contact = this.updatedContacts[this.selectedContactId];
    return this.documentListService
      .updateDocumentContact(
        new ChangeDocumentContact({
          ...contact,
        })
      )
      .pipe(
        map(() => {
          const oldContact = this.documentItem.contacts.find(
            (c) => c.id === contact.id
          );
          const newContact = this.updatedContacts[contact.id];
          newContact.type = Number(newContact.type);
          _.assign(oldContact, newContact);
          return null;
        })
      );
  }

  onSave() {
    if (!this.validateForm()) return;

    this.saveContact()
      .pipe(firstPipe())
      .subscribe(() => {
        this.activeModal.close();
      });
  }

  onRemoveContact() {
    if (!this.newContactIds.some((i) => i === this.selectedContactId))
      this.documentListService
        .removeContact({
          buildingId: this.documentItem.buildingId,
          personId: this.selectedContactId,
        })
        .pipe(firstPipe())
        .subscribe();

    this.documentItem.contacts = this.documentItem.contacts.filter((c) => {
      return c.id !== this.selectedContactId;
    });
    this.selectedContactId = null;
    this.contactForm.markAsUntouched({ onlySelf: true });
  }

  onAddContactBtn() {
    if (!this.validateForm()) return;

    this.contactForm.markAsUntouched({ onlySelf: true });
    this.saveContact()
      .pipe(firstPipe())
      .subscribe(() => {
        this.selectedContactId = null;
        this.addNewContactScreenActive = true;
      });
  }

  private detectChanges() {
    const keysToUpdate = [];
    this.documentItem.contacts.forEach((c) => {
      if (!isEqual({ ...c }, { ...this.updatedContacts[c.id] }))
        keysToUpdate.push(c.id);
    });
    return keysToUpdate;
  }

  onClose() {
    this.newContactIds.forEach((item) => {
      delete this.updatedContacts[item];
      this.documentItem.contacts = this.documentItem.contacts.filter(
        (i) => i.id !== item
      );
    });
    this.activeModal.dismiss("Cross click");
    this.stopTrackingFormChanges.next(true);
  }

  formatPersonType(personType: PersonPersonTypeEnum) {
    switch (personType) {
      case PersonPersonTypeEnum.ACP_VME:
        return $localize`:@@enum.persontype.acpvme:acp/vme`;
      case PersonPersonTypeEnum.Owner:
        return $localize`:@@enum.persontype.owner:Owner`;
      case PersonPersonTypeEnum.Syndic:
        return $localize`:@@enum.persontype.syndic:Syndic`;
      case PersonPersonTypeEnum.Concierge:
        return $localize`:@@enum.persontype.concierge:Concierge`;
      case PersonPersonTypeEnum.Occupant:
        return $localize`:@@enum.persontype.occupant:occupant`;
      case PersonPersonTypeEnum.Present_During_Visit:
        return $localize`:@@enum.persontype.occupant:Present During Visit`;
    }
  }

  onSelectSearchResult(searchResult: Person) {
    let newContact = new DocumentContactDTO({
      id: searchResult.id,
      name: searchResult.name,
      phone: searchResult.phone,
      email: searchResult.email,
      language: this.personLanguageOptions.find(
        (x) => x.displayName == searchResult.language
      ).value,
      type: this.personTypeOptions.find(
        (x) => x.displayName == searchResult.personType
      ).value,
      street: searchResult.address.street,
      number: searchResult.address.number,
      suffix: searchResult.address.suffix,
      postalCode: searchResult.address.postalCode,
      city: searchResult.address.city,
    });
    this.documentListService
      .addContactToList({
        buildingId: this.documentItem.buildingId,
        personId: searchResult.id,
      })
      .pipe(firstPipe())
      .subscribe((res) => {
        this.documentItem.contacts.push(newContact);
        this.updatedContacts[newContact.id] = newContact;
        this.selectedContactId = newContact.id;
        this.onSelectContact(newContact.id);
        this.addNewContactScreenActive = false;
      });
  }

  onNewContact() {
    let newContact = new DocumentContactDTO({
      id: uuid.v4(),
      name: null,
      phone: null,
      email: null,
      language: null,
      type: null,
      street: null,
      suffix: null,
      city: null,
    });
    this.documentItem.contacts.push(newContact);
    this.newContactIds.push(newContact.id);
    this.updatedContacts[newContact.id] = newContact;
    this.selectedContactId = newContact.id;
    this.onSelectContact(newContact.id);
    this.addNewContactScreenActive = false;
  }

  validateForm() {
    if (this.contactForm.touched && !this.contactForm.valid) {
      Object.keys(this.contactForm.controls).forEach((field) => {
        const control = this.contactForm.get(field);
        control.markAsTouched({ onlySelf: true });
      });
      return false;
    }
    return true;
  }
}
